import * as THREE from 'three';

// Keyboard + mouse input with cursor world-space raycast against y=0.

export class Input {
  constructor(canvas, camera) {
    this.canvas = canvas;
    this.camera = camera;
    this.keys = Object.create(null);
    this.mouse = {
      x: 0,
      y: 0,
      screenX: 0,
      screenY: 0,
      ndcX: 0,
      ndcY: 0,
      worldX: 0,
      worldZ: 0,
      leftDown: false,
      rightDown: false,
      leftPressed: false,
      rightPressed: false,
      // Compatibility fields used by older systems.
      down: false,
      pressed: false,
    };
    this.spacePressed = false;
    this.escapePressed = false;
    this.pressed = Object.create(null);

    this._ray = new THREE.Raycaster();
    this._plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this._hit = new THREE.Vector3();
    this._paused = false;
    this._mouseWasDownLeft = false;
    this._mouseWasDownRight = false;

    this._onKeyDown = (e) => {
      if (e.repeat) return;
      this.keys[e.code] = true;
      this.pressed[e.code] = true;
      if (e.code === 'Space') this.spacePressed = true;
      if (e.code === 'Escape') this.escapePressed = true;
    };
    this._onKeyUp = (e) => { this.keys[e.code] = false; };
    this._onBlur = () => {
      this.keys = Object.create(null);
      this.pressed = Object.create(null);
      this.mouse.leftDown = false;
      this.mouse.rightDown = false;
      this.mouse.down = false;
    };
    this._onMove = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.mouse.screenX = e.clientX;
      this.mouse.screenY = e.clientY;
      this.mouse.x = e.clientX - rect.left;
      this.mouse.y = e.clientY - rect.top;
      this.mouse.ndcX = (this.mouse.x / rect.width) * 2 - 1;
      this.mouse.ndcY = -((this.mouse.y / rect.height) * 2 - 1);
    };
    this._onDown = (e) => {
      if (e.button === 0) this.mouse.leftDown = true;
      if (e.button === 2) this.mouse.rightDown = true;
    };
    this._onUp = (e) => {
      if (e.button === 0) this.mouse.leftDown = false;
      if (e.button === 2) this.mouse.rightDown = false;
    };
    this._onContext = (e) => e.preventDefault();

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('blur', this._onBlur);
    this.canvas.addEventListener('mousemove', this._onMove);
    this.canvas.addEventListener('mousedown', this._onDown);
    window.addEventListener('mouseup', this._onUp);
    this.canvas.addEventListener('contextmenu', this._onContext);
  }

  setPaused(p) { this._paused = p; }

  moveAxis() {
    if (this._paused) return { x: 0, z: 0 };
    let x = 0, z = 0;
    if (this.keys['KeyW'] || this.keys['ArrowUp']) z -= 1;
    if (this.keys['KeyS'] || this.keys['ArrowDown']) z += 1;
    if (this.keys['KeyA'] || this.keys['ArrowLeft']) x -= 1;
    if (this.keys['KeyD'] || this.keys['ArrowRight']) x += 1;
    const len = Math.hypot(x, z);
    if (len > 1) { x /= len; z /= len; }
    return { x, z };
  }

  consumeSpace() { const v = this.spacePressed; this.spacePressed = false; return v; }
  consumeEscape() { const v = this.escapePressed; this.escapePressed = false; return v; }
  consumePickupEquipPressed() { return this.consumeKey('KeyE'); }
  consumePickupDiscardPressed() { return this.consumeKey('KeyF'); }
  consumeEquipPressed() { return this.consumeKey('KeyE') || this.consumeKey('KeyF'); }
  consumeMedkitPressed() { return this.consumeKey('KeyH'); }
  isDown(code) { return !!this.keys[code]; }
  consumeKey(code) {
    const v = !!this.pressed[code];
    this.pressed[code] = false;
    return v;
  }

  updateCursorWorld() {
    this._ray.setFromCamera({ x: this.mouse.ndcX, y: this.mouse.ndcY }, this.camera);
    if (this._ray.ray.intersectPlane(this._plane, this._hit)) {
      this.mouse.worldX = this._hit.x;
      this.mouse.worldZ = this._hit.z;
    }
    // Detect pressed edge (button just went down this frame)
    this.mouse.leftPressed = this.mouse.leftDown && !this._mouseWasDownLeft;
    this.mouse.rightPressed = this.mouse.rightDown && !this._mouseWasDownRight;
    // Keep compatibility fields mapped to left button.
    this.mouse.down = this.mouse.leftDown;
    this.mouse.pressed = this.mouse.leftPressed;
    this._mouseWasDownLeft = this.mouse.leftDown;
    this._mouseWasDownRight = this.mouse.rightDown;
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
    this.canvas.removeEventListener('mousemove', this._onMove);
    this.canvas.removeEventListener('mousedown', this._onDown);
    window.removeEventListener('mouseup', this._onUp);
    this.canvas.removeEventListener('contextmenu', this._onContext);
  }
}
